/**
 * How usage counts leave the machine — deliberately the dumbest transport in
 * the app.
 *
 * It uses bare `fetch`, NOT `utils/apiClient` or `services/apiService`. Both of
 * those run `attachAuthHeaders()`, which adds `Authorization` and
 * **`X-Client-Id`** — the per-install UUID minted for realtime echo
 * suppression. Sending that alongside an anonymous counter would hand the
 * server exactly the identifier this whole design exists to avoid, and would
 * reuse an id collected for an unrelated purpose. Do not "simplify" this onto
 * the shared client.
 *
 * The payload is feature keys, the app version and the OS — nothing else.
 * 1.0.6–1.1.1 also sent the install licence key; that licence was removed on
 * 26.10.2026 (login-only), so a batch carries no identifier at all again.
 * `X-Client-Id` and the session never ride along, for the reason above.
 * Nothing here is sent at all while "Send usage data" is off.
 *
 * Failures are silent by design. A counter must never retry into a hot loop,
 * never surface an error to the user and never delay anything: batches are
 * dropped on failure rather than queued to disk, because a lost count is
 * cheaper than a growing spool of pending telemetry on someone's machine.
 */

const BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.apiboo.com/api/v1';
const ENDPOINT = `${BASE}/app/usage`;

/**
 * Ceiling on one request. Anything beyond it is DROPPED, not deferred — the
 * caller has already claimed those keys' daily marks, so they will not be
 * offered again today. Unreachable while the whole allow-list is smaller than
 * this; if the list ever grows past it, make the caller chunk instead.
 */
const MAX_KEYS = 50;

/**
 * Post a batch of feature keys.
 *
 * @param {string[]} keys      already validated against the allow-list
 * @param {{ appVersion?: string, os?: string }} env
 * @returns {Promise<boolean>} whether the server accepted it; never rejects
 */
export async function sendUsage(keys, env = {}) {
  if (!Array.isArray(keys) || keys.length === 0) return true;
  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      // no credentials, no cookies: this request must not carry a session
      credentials: 'omit',
      cache: 'no-store',
      keepalive: true,
      body: JSON.stringify({
        features: keys.slice(0, MAX_KEYS),
        app_version: env.appVersion || 'unknown',
        os: env.os || 'unknown',
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}
